import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Search } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = "hp_admin_token";
const adminHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}` });

const ACTION_LABELS = {
  register: "Registratie",
  login: "Login",
  google_login: "Google login",
  denied: "Geweigerd",
  logout: "Uitloggen",
};

const ACTION_STYLES = {
  register: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  login: "bg-sky-500/20 text-sky-300 border-sky-500/30",
  google_login: "bg-indigo-500/20 text-indigo-300 border-indigo-500/30",
  denied: "bg-rose-500/20 text-rose-300 border-rose-500/30",
  logout: "bg-slate-500/20 text-slate-300 border-slate-500/30",
};

function formatDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("nl-NL", {
      dateStyle: "short", timeStyle: "medium",
    });
  } catch {
    return iso;
  }
}

export const AuthEvents = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [action, setAction] = useState("");
  const [email, setEmail] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        per_page: "50",
      });
      if (action) params.set("action", action);
      if (email) params.set("email", email);
      if (success) params.set("success", success);
      const res = await axios.get(`${API}/admin/auth-events?${params.toString()}`, {
        headers: adminHeaders(),
      });
      setEvents(res.data.events || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/sportly-admin", { replace: true });
      } else {
        toast.error("Kon audit log niet laden");
      }
    } finally {
      setLoading(false);
    }
  }, [page, action, email, success, navigate]);

  useEffect(() => {
    if (!localStorage.getItem(ADMIN_TOKEN_KEY)) {
      navigate("/sportly-admin", { replace: true });
      return;
    }
    load();
  }, [load, navigate]);

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    navigate("/sportly-admin", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="auth-events-page">
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/sportly-admin/dashboard")}
              className="text-slate-400 hover:text-white flex items-center gap-1.5"
              data-testid="events-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Admin</span>
            </button>
            <span className="text-slate-700">/</span>
            <span className="font-bold text-sm">Audit log</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="events-logout-btn"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6 space-y-4">
        <div className="grid gap-2 sm:grid-cols-4">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder="E-mail zoeken"
              value={email}
              onChange={(e) => { setPage(1); setEmail(e.target.value); }}
              className="bg-slate-900 border-slate-700 text-white pl-9 h-9"
              data-testid="events-email-filter"
            />
          </div>
          <select
            value={action}
            onChange={(e) => { setPage(1); setAction(e.target.value); }}
            className="bg-slate-900 border border-slate-700 text-white rounded-md h-9 px-2 text-sm"
            data-testid="events-action-filter"
          >
            <option value="">Alle acties</option>
            <option value="register">Registratie</option>
            <option value="login">Login</option>
            <option value="google_login">Google login</option>
            <option value="denied">Geweigerd</option>
          </select>
          <select
            value={success}
            onChange={(e) => { setPage(1); setSuccess(e.target.value); }}
            className="bg-slate-900 border border-slate-700 text-white rounded-md h-9 px-2 text-sm"
            data-testid="events-success-filter"
          >
            <option value="">Alle resultaten</option>
            <option value="true">Alleen succes</option>
            <option value="false">Alleen mislukt</option>
          </select>
          <div className="text-right text-xs text-slate-400 self-center">
            {loading ? "Laden..." : `${total} events`}
          </div>
        </div>

        <div className="overflow-x-auto rounded-md border border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-400">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Tijdstip</th>
                <th className="text-left px-3 py-2 font-medium">Actie</th>
                <th className="text-left px-3 py-2 font-medium">Resultaat</th>
                <th className="text-left px-3 py-2 font-medium">E-mail</th>
                <th className="text-left px-3 py-2 font-medium">IP</th>
                <th className="text-left px-3 py-2 font-medium">Detail</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev, i) => (
                <tr key={i} className="border-t border-slate-800 hover:bg-slate-900/50" data-testid={`auth-event-row-${i}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-300">{formatDate(ev.created_at)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded border text-xs ${ACTION_STYLES[ev.action] || "bg-slate-800 border-slate-700"}`}>
                      {ACTION_LABELS[ev.action] || ev.action}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={ev.success ? "text-emerald-400" : "text-rose-400"}>
                      {ev.success ? "✓" : "✗"}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-200">{ev.email || "-"}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-400">{ev.ip || "-"}</td>
                  <td className="px-3 py-2 text-xs text-slate-400 max-w-xs truncate" title={ev.detail}>
                    {ev.detail || "-"}
                  </td>
                </tr>
              ))}
              {!loading && events.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-slate-500">Geen events gevonden.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between text-xs text-slate-400">
          <div>Pagina {page} van {totalPages}</div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-300 h-8"
              data-testid="events-prev-btn"
            >
              Vorige
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-300 h-8"
              data-testid="events-next-btn"
            >
              Volgende
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AuthEvents;
